import { useContext, useEffect, useMemo, useState } from 'react';

import { logError } from 'commons/logger';

import PostHogFeatureFlagsContext from './PostHogFeatureFlagsContext';
import { getAppPostHogClient } from './appPostHogClient';

const postHogOverrideState = { hasOverridden: false };
const reportedExposures = new Set();

// The app client starts after LCP, up to 8s plus idle time when LCP is unobservable
const POSTHOG_INIT_MAX_WAIT_MS = 30_000;
const POSTHOG_INIT_POLL_MS = 100;

const getCurrentViewKey = () =>
  `${window.location.pathname}${window.location.search}`;

// `clear` and bad input both return false, which drops any overrides.
// `error` is set on bad input, for the caller that logs it.
const parsePostHogQueryQuietly = query => {
  try {
    const queryObject = JSON.parse(query);

    if (queryObject?.clear === true) {
      return { value: false };
    }

    return { value: queryObject };
  } catch (err) {
    return { value: false, error: err };
  }
};

// Only for the effect. Logging during render would hit Sentry on every
// render, and twice in StrictMode.
const parsePostHogQuery = query => {
  const { value, error } = parsePostHogQueryQuietly(query);

  if (error) {
    logError(error, 'Unable to parse posthogOverrides query', {
      query,
    });
  }

  return value;
};

const applyPostHogOverrides = posthog => {
  const postHogOverrideQuery = new URLSearchParams(window.location.search).get(
    'posthogOverrides'
  );

  if (
    postHogOverrideState.hasOverridden ||
    !postHogOverrideQuery ||
    postHogOverrideQuery === '' ||
    postHogOverrideQuery === '{}' ||
    !posthog?.featureFlags
  ) {
    return;
  }

  const parsedQuery = parsePostHogQuery(postHogOverrideQuery);
  if (parsedQuery !== false) {
    postHogOverrideState.hasOverridden = true;
    posthog.featureFlags.override(parsedQuery);
  }
};

// ?posthogOverrides wins over the server value. Otherwise the page shows one
// variant and reports the other.
// Skips the hasOverridden latch on purpose: that guards a once-per-session
// mutation, while this read must return the same value on every render.
const readOverrideValue = (name, search) => {
  const query = new URLSearchParams(search).get('posthogOverrides');

  if (!query || query === '' || query === '{}') {
    return undefined;
  }

  const { value } = parsePostHogQueryQuietly(query);
  return value === false ? undefined : value?.[name];
};

const readLiveFlagState = (posthog, name) => {
  if (!posthog?.isFeatureEnabled(name)) {
    return { value: null, payload: null };
  }

  const val = posthog.getFeatureFlag(name);
  const payloadValue = posthog.getFeatureFlagPayload(name);

  if (val === null) {
    return { value: null, payload: null };
  }

  return { value: val, payload: payloadValue };
};

const buildExposureKey = (posthog, name, distinctId) => {
  const resolvedDistinctId =
    distinctId || posthog?.get_distinct_id?.() || 'unknown-distinct-id';
  return `${name}::${resolvedDistinctId}::${getCurrentViewKey()}`;
};

const reportFeatureFlagExposure = (posthog, name, distinctId) => {
  if (!posthog) {
    return;
  }

  const exposureKey = buildExposureKey(posthog, name, distinctId);
  if (reportedExposures.has(exposureKey)) {
    return;
  }

  reportedExposures.add(exposureKey);
  posthog.getFeatureFlag(name);
};

// Not window.posthog: RudderStack's instance can hold it without the seed
const waitForPostHog = onReady => {
  if (getAppPostHogClient()) {
    onReady(getAppPostHogClient());
    return () => {};
  }

  const startedAt = Date.now();
  const intervalId = setInterval(() => {
    if (getAppPostHogClient()) {
      clearInterval(intervalId);
      onReady(getAppPostHogClient());
      return;
    }

    if (Date.now() - startedAt >= POSTHOG_INIT_MAX_WAIT_MS) {
      clearInterval(intervalId);
    }
  }, POSTHOG_INIT_POLL_MS);

  return () => {
    clearInterval(intervalId);
  };
};

const usePostHogFeatureFlagWithPayload = name => {
  const seededContext = useContext(PostHogFeatureFlagsContext);
  const seededValue = seededContext?.featureFlags?.[name];
  const seededDistinctId = seededContext?.distinctId;
  // Admin mirrors this folder into its CRA build, which does not polyfill
  // ES2022 built-ins such as Object.hasOwn.
  // eslint-disable-next-line prefer-object-has-own
  const hasSeededValue = Object.prototype.hasOwnProperty.call(
    seededContext?.featureFlags || {},
    name
  );

  const search = typeof window === 'undefined' ? '' : window.location.search;
  const overrideValue = useMemo(() => readOverrideValue(name, search), [
    name,
    search,
  ]);
  const seedValueToUse =
    overrideValue === undefined ? seededValue : overrideValue;

  // The server cannot read the query string, so the first render has to use
  // the server value. The effect below applies the override straight after.
  const [value, setValue] = useState(() =>
    hasSeededValue ? seededValue : null
  );
  const [payload, setPayload] = useState(null);
  // Off and not-loaded flags both read as null
  const [hasLoadedFlags, setHasLoadedFlags] = useState(hasSeededValue);

  useEffect(() => {
    if (hasSeededValue) {
      setValue(seedValueToUse);
    }
  }, [hasSeededValue, seedValueToUse]);

  useEffect(() => {
    if (!name || hasSeededValue) {
      return undefined;
    }

    const removePostHogEventListeners = [];

    const subscribeToPostHog = posthog => {
      applyPostHogOverrides(posthog);

      const removePostHogEventListener = posthog.onFeatureFlags(() => {
        applyPostHogOverrides(posthog);

        const nextState = readLiveFlagState(posthog, name);
        if (nextState.value !== null) {
          setValue(nextState.value);
          setPayload(nextState.payload);
        }
        setHasLoadedFlags(true);
      });

      removePostHogEventListeners.push(removePostHogEventListener);

      // The exposure for a cached value is already sent
      const initialState = readLiveFlagState(posthog, name);
      if (initialState.value !== null) {
        setValue(initialState.value);
        setPayload(initialState.payload);
        setHasLoadedFlags(true);
      }
    };

    const cancelWait = waitForPostHog(subscribeToPostHog);

    return () => {
      cancelWait();

      removePostHogEventListeners.forEach(removePostHogEventListener => {
        removePostHogEventListener?.();
      });
    };
  }, [hasSeededValue, name]);

  useEffect(() => {
    if (!name || !hasSeededValue || typeof window === 'undefined') {
      return undefined;
    }

    const reportExposure = posthog => {
      applyPostHogOverrides(posthog);
      reportFeatureFlagExposure(posthog, name, seededDistinctId);
    };

    return waitForPostHog(reportExposure);
  }, [hasSeededValue, name, seededDistinctId]);

  return useMemo(() => [value, payload, hasLoadedFlags], [
    hasLoadedFlags,
    payload,
    value,
  ]);
};

export const resetPostHogFeatureFlagTestState = () => {
  postHogOverrideState.hasOverridden = false;
  reportedExposures.clear();
};

export default usePostHogFeatureFlagWithPayload;
